/**
 * FUNCTIONS:
 * - debouncer(func, timeout)
 * - throttle(func, wait)
 * - documentReady(callbackFunction)
 * - slideUp(target, duration=500)
 * - slideDown(target, duration=500)
 * - slideToggle(target, duration = 500)
 * - index(el)
 * - scrollToEl(targetEl)
 * - toggleClasOnScroll(selector, toggleClass)
 * - observeElementResize(element, callback, options)
 * - unobserveElementResize(element, callback)
 * - clearElementResize(element);
 */

/**
 * used for window resize/scroll functions to reduce function calls
 *
 * @param func {function(*): *} - The function to be debounced
 * @param [timeout=20] {Number} - number of milliseconds to wait before calling.
 * @returns {(function(): void)} - the debounced function
 */
export function debouncer(func, timeout= 20) {
  let timeoutID;
  return function (...args) {
    const scope = this;
    clearTimeout(timeoutID);
    timeoutID = setTimeout(function () {
      func.apply(scope, [...args]);
    }, timeout);
  };
}

/**
 * Throttles a function call to happen only once every {wait} milliseconds
 *
 * @param fn {function(*?): void|*} - function to throttle
 * @param wait {number} - milliseconds to wait between calls.
 * @param [trailing=true] {boolean} - make a trailing call so that the last call will always get called
 * @returns {function(*?): void} - the throttled function
 */
export function throttle(fn, wait, trailing=true) {

  let time = Date.now();
  let timeoutID;
  return function (...e) {
    const timeLeft = time + wait - Date.now();
    const callFn = ()=>{
      clearTimeout(timeoutID);
      time = Date.now();
      fn.call(this,...e);
    }
    if (timeLeft <= 0) {
      callFn();
    }else if(trailing) {
      // So that the last call will always get called.
      timeoutID = setTimeout(callFn,timeLeft+1);
    }

  };
}



let DOMContentLoadedEvent;
document.addEventListener("DOMContentLoaded", (e)=>DOMContentLoadedEvent = e);

/**
 * Document ready function
 *
 * @param callbackFunction {function(*):*} - function to be called when the document is ready.
 */
export function documentReady(callbackFunction){
  if(document.readyState !== 'loading')
    callbackFunction(DOMContentLoadedEvent);
  else
    document.addEventListener("DOMContentLoaded", callbackFunction)
}

/* SLIDE UP */
/**
 * Slide an element up
 * @param target {HTMLElement}  - the element to be moved
 * @param [duration=500] {number} - the duration of the move in milliseconds;
 */
export function slideUp(target, duration=500) {
  target.style.transitionProperty = 'height, margin, padding';
  target.style.transitionDuration = duration + 'ms';
  target.style.boxSizing = 'border-box';
  target.style.height = target.offsetHeight + 'px';
  target.offsetHeight;
  target.style.overflow = 'hidden';
  target.style.height = 0;
  target.style.paddingTop = 0;
  target.style.paddingBottom = 0;
  target.style.marginTop = 0;
  target.style.marginBottom = 0;
  window.setTimeout( () => {
    target.style.display = 'none';
    target.style.removeProperty('height');
    target.style.removeProperty('padding-top');
    target.style.removeProperty('padding-bottom');
    target.style.removeProperty('margin-top');
    target.style.removeProperty('margin-bottom');
    target.style.removeProperty('overflow');
    target.style.removeProperty('transition-duration');
    target.style.removeProperty('transition-property');
    target.style.removeProperty('box-sizing');
    //alert("!");
  }, duration);
}

/**
 * Slide an element down
 * @param target {HTMLElement}  - the element to be moved
 * @param [duration=500] {number} - the duration of the move in milliseconds;
 */
export function slideDown(target, duration=500) {
  target.style.removeProperty('display');
  let display = window.getComputedStyle(target).display;
  if (display === 'none') display = 'block';
  target.style.display = display;
  let height = target.offsetHeight;
  target.style.overflow = 'hidden';
  target.style.height = 0;
  target.style.paddingTop = 0;
  target.style.paddingBottom = 0;
  target.style.marginTop = 0;
  target.style.marginBottom = 0;
  target.offsetHeight;
  target.style.boxSizing = 'border-box';
  target.style.transitionProperty = "height, margin, padding";
  target.style.transitionDuration = duration + 'ms';
  target.style.height = height + 'px';
  target.style.removeProperty('padding-top');
  target.style.removeProperty('padding-bottom');
  target.style.removeProperty('margin-top');
  target.style.removeProperty('margin-bottom');
  window.setTimeout( () => {
    target.style.removeProperty('height');
    target.style.removeProperty('overflow');
    target.style.removeProperty('transition-duration');
    target.style.removeProperty('transition-property');
    target.style.removeProperty('box-sizing');
  }, duration);
}

/**
 * Toggle an element by sliding it in and out
 *
 * @param target {HTMLElement}  - the element to be moved
 * @param [duration=500] {number} - the duration of the move in milliseconds;
 */
export function slideToggle(target, duration = 500) {
  if (window.getComputedStyle(target).display === 'none') {
    return slideDown(target, duration);
  } else {
    return slideUp(target, duration);
  }
}

/**
 * Find the index of a given element
 * @param el {HTMLElement} - the element to find the index of.
 * @returns {number} the index of the element.
 */
export function index(el) {
  if (!el) return -1;
  let i = 0;
  do {
    i++;
    el = el.previousElementSibling;
  } while (el);
  return i;
}


/**
 * Smooth scroll to element
 *
 * @param targetEl {HTMLElement} - element to scroll to
 */
export function scrollToEl(targetEl){
  const currentScroll = window.pageYOffset | document.body.scrollTop;
  const targetPos = Math.round(targetEl.getBoundingClientRect().top);
  const adminBarEl = document.getElementById("wpadminbar");
  const adminBarHeight = !!adminBarEl ? adminBarEl.offsetHeight : 0;
  const scrollPos = targetPos - adminBarHeight + currentScroll - 20;
  scroll({
    top: scrollPos,
    behavior: "smooth",
  });
}

/**
 * Toggles a class on/off on scroll up/down. Useful for disappearing headers.
 *
 * @param  selector {string} - CSS selector of the element to toggle things on
 * @param toggleClass {string} - class to be added/removed
 * @param [topClass=''] {string} - class to be added when at top of page
 * @returns {(function(): void)|*} returns a function to remove the event listener
 */
export function toggleClassOnScroll(selector, toggleClass, topClass='') {

  const toggledElement = document.querySelector(selector);

  let lastScroll = window.scrollY;

  const handleScroll = throttle(function() {
    const scrollY = window.scrollY;

    if(scrollY <= toggledElement.clientHeight){
      toggledElement.classList.add(topClass);
    } else {
      toggledElement.classList.remove(topClass);
    }

    if (lastScroll < scrollY && scrollY > toggledElement.clientHeight) {
      toggledElement.classList.add(toggleClass);
    }
    if (lastScroll > scrollY || scrollY <= toggledElement.clientHeight) {
      toggledElement.classList.remove(toggleClass);
    }

    lastScroll = scrollY;
  }, 300);

  handleScroll();


  window.addEventListener("scroll", handleScroll, {
    passive: true
  });

  /**
   * Cleans up the element by removing the class and also removes the event listener.
   */
  function toggleClassOnScrollCleanup(){
    window.removeEventListener("scroll", handleScroll);
    toggledElement.classList.remove(toggleClass);
  }
  return toggleClassOnScrollCleanup;

}

const ResizeObserverMap = new Map();

const GlobalResizeObserver = new ResizeObserver((entries, observer)=>{
  entries.forEach(entry=>{
    const callbackSet = ResizeObserverMap.get(entry.target);
    callbackSet.forEach((fn)=>fn(entry, observer));
  })
});


/**
 * @callback GlobalResizeObserverCallback
 * @param {ResizeObserverEntry} entry
 * @param {GlobalResizeObserver} observer - the GlobalResizeObserver. Be very careful what you do with this.
 */

/**
 * Add an element to be observed by the GlobalResizeObserver object
 * @param {Element} element - element to be observed
 * @param {GlobalResizeObserverCallback} callback - callback to be added when element is observed.
 * @param {ResizeObserverOptions} [options] - options for the observe method.
 */
export function observeElementResize(element, callback, options){
  const callbackSet = ResizeObserverMap.get(element) || new Set();
  if(!callbackSet.has(callback)){
    callbackSet.add(callback);
  }
  ResizeObserverMap.set(element, callbackSet);
  GlobalResizeObserver.observe(element, options);
}

/**
 * Remove a specific callback from being observed for an element
 * @param {Element} element
 * @param {GlobalResizeObserverCallback} callback
 * @return {Boolean} returns if the callback was removed or not.
 */
export function unobserveElementResize(element, callback){
  let result = false;
  if(ResizeObserverMap.has(element)){
    const callbackSet = ResizeObserverMap.get(element);
    result = callbackSet.delete(callback);
    if(callbackSet.size() === 0){
      clearElementResize(element);
    }
  }
  return result;
}

/**
 * Removes ALL callbacks for a given element and stops observing it.
 * @param {Element} element - element to be cleared from the resize observer.
 */
export function clearElementResize(element){
  if(ResizeObserverMap.has(element)){
    const callbackSet = ResizeObserverMap.get(element);
    callbackSet.clear();
    ResizeObserverMap.delete(element);
    GlobalResizeObserver.unobserve(element);
  }
}
